import React from "react";
import ReactSelect from "react-select";
import type { Props as ReactSelectProps } from "react-select";
import styles from "./Select.module.scss";
import withLabelAndError from "../withLabelAndError";
import { Controller } from "react-hook-form";
import { combineClasses } from "@/lib/utils";
import LoadingText from "@/components/LoadingText/LoadingText";

interface SelectOption {
  label: React.ReactNode;
  value: string;
}

interface SelectProps extends ReactSelectProps<SelectOption, boolean> {
  control?: any;
  validations?: any;
  name: string;
  optionsHeight?: number;
  className?: string;
  loadingText?: string;
  isLoading?: boolean;
}

const Select: React.FC<SelectProps> = (props) => {
  const {
    control,
    validations,
    name,
    placeholder,
    isDisabled,
    options,
    optionsHeight,
    isMulti,
    className = "",
    loadingText = "Loading",
    isLoading = false,
    styles: customStylesProp,
    ...rest
  } = props;

  const loadingOption: SelectOption = {
    label: <LoadingText text={loadingText} style={{ fontWeight: "400" }} />,
    value: "loading",
  };

  const customStyles = {
    ...customStylesProp,
    control: (provided: any, state: any) => ({
      ...provided,
      background: "none",
      border: "none",
      minHeight: "28px",
      height: isMulti ? "maxContent" : "28px",
      boxShadow: "none",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      flexWrap: "nowrap",
      ...(customStylesProp?.control ? customStylesProp.control(provided, state) : {}),
    }),
    valueContainer: (provided: any, state: any) => ({
      ...provided,
      height: "28px",
      padding: "0 4px",
      margin: 0,
      display: "flex",
      alignItems: "center",
      ...(customStylesProp?.valueContainer ? customStylesProp.valueContainer(provided, state) : {}),
    }),
    singleValue: (provided: any, state: any) => ({
      ...provided,
      margin: 0,
      padding: 0,
      lineHeight: "28px",
      color: "inherit",
      ...(customStylesProp?.singleValue ? customStylesProp.singleValue(provided, state) : {}),
    }),
    input: (provided: any, state: any) => ({
      ...provided,
      margin: 0,
      padding: 0,
      ...(customStylesProp?.input ? customStylesProp.input(provided, state) : {}),
    }),
    indicatorsContainer: (provided: any, state: any) => ({
      ...provided,
      height: "28px",
      ...(customStylesProp?.indicatorsContainer ? customStylesProp.indicatorsContainer(provided, state) : {}),
    }),
    dropdownIndicator: (provided: any, state: any) => ({
      ...provided,
      padding: "2px",
      ...(customStylesProp?.dropdownIndicator ? customStylesProp.dropdownIndicator(provided, state) : {}),
    }),
    indicatorSeparator: (provided: any, state: any) => ({
      display: "none",
      ...(customStylesProp?.indicatorSeparator ? customStylesProp.indicatorSeparator(provided, state) : {}),
    }),
    menuPortal: (provided: any, state: any) => ({
      ...provided,
      ...(customStylesProp?.menuPortal ? customStylesProp.menuPortal(provided, state) : {}),
    }),
  };

  const renderInput = (field: any = {}) => (
    <ReactSelect<SelectOption, boolean>
      {...rest}
      {...field}
      id={name}
      className={combineClasses(styles.inputField, className)}
      value={isLoading ? loadingOption : field?.value ?? rest?.value}
      options={isLoading ? [loadingOption] : options}
      styles={customStyles}
      placeholder={placeholder}
      classNamePrefix="react-select"
      isDisabled={isDisabled || isLoading}
      isMulti={isMulti}
      // getOptionLabel={(option) =>
      //   typeof option.label === "string" ? option.label : String(option.value)
      // }
      // getOptionValue={(option) => option.value}    getOptionValue={(option) => option.value}
      // 👇 this renders JSX labels correctly
      getOptionValue={(option) => option.value}
      formatOptionLabel={(option) => {
        if (typeof option.label === "string") return option.label;

        if (typeof option.label === "object" && option.label !== null) {
          return option.label || JSON.stringify(option.label);
        }

        return String(option.label);
      }}
    />
  );

  if (control) {
    return (
      <Controller
        control={control}
        rules={validations}
        name={name}
        render={({ field }) => renderInput(field)}
      />
    );
  } else {
    return renderInput();
  }
};

const SelectWithLabelAndError = withLabelAndError(Select);

export default SelectWithLabelAndError;
export { Select };
